/* MobileCases — mobile-optimized cases page.
   Header + background match the MobileChat ("مساعد LexIQ") aesthetic:
   - White → #DCDCDC gradient.
   - Fixed top bar (portal-rendered): circular back button | title + subtitle | circular action.
   - Search field for ref number / case number / plaintiff / defendant name.
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  CaretRight, MagnifyingGlass, Plus, XIcon,
} from "@phosphor-icons/react";
import { useCases } from "../../lib/casesStore";
import { EmptyState, NoResults, Skeleton } from "./MobileCasesStates";
import CaseCard from "./CaseCard";

/* ============================== Fixed top bar ============================ */
function CasesHeader({ headerRef, count, showSearch, searchProps }) {
  return createPortal(
    <header
      ref={headerRef}
      className="lg:hidden fixed top-0 inset-x-0 z-30"
      style={{
        paddingTop: "calc(max(env(safe-area-inset-top, 0px), 44px) + 4px)",
        paddingBottom: "6px",
        // RULE 1: 10% white + heavy frosted blur — owns the dead-zone.
        background: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(22px) saturate(180%)",
        WebkitBackdropFilter: "blur(22px) saturate(180%)",
      }}
      data-testid="mobile-cases-header"
    >
      <div className="px-4 flex items-center justify-between">
        <Link
          to="/"
          data-testid="mobile-cases-back"
          aria-label="رجوع للرئيسية"
          className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(10,25,47,0.10)] flex items-center justify-center active:scale-95 transition-transform"
        >
          <CaretRight size={20} weight="bold" className="text-[#0A192F]" />
        </Link>
        <div className="flex-1 text-center px-3">
          <h2 className="text-[17px] font-bold text-[#0A192F] leading-tight">قضاياي</h2>
          <p className="text-[11px] text-[#5A6470] mt-0.5">{count} قضية محفوظة</p>
        </div>
        <Link
          to="/analyze"
          data-testid="mobile-cases-new"
          aria-label="قضية جديدة"
          className="w-10 h-10 rounded-full bg-[#0A192F] text-white flex items-center justify-center active:scale-95 transition-transform shadow-md"
        >
          <Plus size={18} weight="bold" />
        </Link>
      </div>
      {showSearch && (
        <div className="px-4 pt-2.5">
          <SearchBar {...searchProps} />
        </div>
      )}
    </header>,
    document.body,
  );
}

/* ============================== Search bar =============================== */
function SearchBar({ value, onChange, resultCount, totalCount }) {
  return (
    <div>
      <div className="relative" data-testid="mobile-cases-search">
        <MagnifyingGlass
          size={18} weight="duotone"
          className="absolute end-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="مسح البحث"
            className="absolute start-2 top-1/2 -translate-y-1/2 p-1 text-[#8A8A8A] hover:text-[#0A192F]"
            data-testid="mobile-cases-search-clear"
          >
            <XIcon size={14} weight="bold" />
          </button>
        )}
        <input
          type="search"
          dir="rtl"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ابحث برقم القضية أو الاسم..."
          className="w-full bg-white/85 border border-[#EAEAEA] rounded-2xl ps-9 pe-10 py-3 text-[16px] focus:outline-none focus:border-[#D4AF37] placeholder:text-[#9A9A9A]"
          data-testid="mobile-cases-search-input"
        />
      </div>
      {value && (
        <p className="text-[12px] text-[#5A6470] mt-1.5 px-1">
          {resultCount} من {totalCount} قضية تطابق بحثك
        </p>
      )}
    </div>
  );
}

/* probBand + ProbabilityGauge moved to ./ProbabilityGauge.jsx */
/* CaseCard moved to ./CaseCard.jsx (date helpers + card markup) */

/* =============================== Main view =============================== */
export default function MobileCases({ onView, onDelete }) {
  // P0 — Use the shared store instead of a local `useEffect(listCases)`.
  // Previously the desktop parent and this component EACH fetched the
  // list on every mount → 2 identical HTTP calls in parallel per visit.
  // The store dedupes concurrent readers and caches for 30 s.
  const { cases, loading } = useCases();
  const [query, setQuery] = useState("");
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(72);

  // Match the body bg + iOS status-bar to the white top of the gradient
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#FFFFFF";
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#FFFFFF");
    return () => {
      document.body.style.backgroundColor = prevBg;
      if (meta && prevTheme !== null) meta.setAttribute("content", prevTheme);
    };
  }, []);

  // Delegate delete to the parent so the shared store gets invalidated
  // consistently for both the desktop table and this mobile list.
  const handleDelete = async (id) => {
    if (!onDelete) return;
    await onDelete(id);
  };

  // Multi-field fuzzy filter: ref_number, title, parties (plaintiff/defendant).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) => {
      const haystack = [
        c.ref_number || "",
        c.id || "",
        c.title || "",
        c.parties || "",
        c.analysis?.case_type || "",
        c.analysis?.competent_court || "",
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [cases, query]);

  // Measure live header height (it grows when the search bar is rendered
  // and when the "x of y match" hint appears) so the scroll content offset
  // stays exactly flush below the fixed bar.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cases.length, query]);

  const showSearch = cases.length > 0;

  return (
    <>
      <div
        className="lg:hidden fixed pointer-events-none -z-10"
        style={{
          top: "-20vh", bottom: "-20vh", left: 0, right: 0,
          background:
            "linear-gradient(180deg,#FFFFFF 0%,#FAFAFA 22%,#F3F3F3 44%,#ECECEC 64%,#E4E4E4 82%,#DCDCDC 100%)",
        }}
        aria-hidden="true"
      />

      <CasesHeader
        headerRef={headerRef}
        count={cases.length}
        showSearch={showSearch}
        searchProps={{
          value: query,
          onChange: setQuery,
          resultCount: filtered.length,
          totalCount: cases.length,
        }}
      />

      <div
        className="lg:hidden min-h-[100dvh] px-4 pb-6 font-tajawal"
        style={{ paddingTop: `${headerH + 8}px` }}
        data-testid="mobile-cases"
      >
        {loading ? (
          <Skeleton />
        ) : cases.length === 0 ? (
          <EmptyState />
        ) : filtered.length === 0 ? (
          <NoResults query={query} onClear={() => setQuery("")} />
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <CaseCard key={c.id} c={c} onDelete={handleDelete} onView={onView} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
